import { ITopicOverviewCard } from "@/api";
import { styles } from "@/config/project";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ListAltIcon from "@mui/icons-material/ListAlt";
import {
    alpha,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Typography,
    useTheme,
} from "@mui/material";
import { useState } from "react";
interface TopicCardProps {
    topic: ITopicOverviewCard;
    onViewDetails: () => void;
    showGaps?: boolean;
}

export const TopicCard = ({
    topic,
    onViewDetails,
    showGaps = true,
}: TopicCardProps) => {
    const theme = useTheme();
    const [fieldsExpanded, setFieldsExpanded] = useState(false);
    const [gapsExpanded, setGapsExpanded] = useState(false);

    const hasGaps = topic.gaps.length > 0;
    const hasFields = topic.top_fields.length > 0;
    const hasSummary = topic.summary && topic.summary.length > 0;

    // Sort gaps by priority if available
    const sortedGaps = [...topic.gaps].sort(
        (a, b) => (a.priority ?? 99) - (b.priority ?? 99)
    );

    const visibleGapsCount = 3;
    const hasMoreGaps = sortedGaps.length > visibleGapsCount;
    const hiddenGaps = sortedGaps.slice(visibleGapsCount);

    // Fields: show first 3 as "key", rest expandable
    const keyFieldsCount = 3;
    const hasMoreFields = topic.top_fields.length > keyFieldsCount;
    const hiddenFields = topic.top_fields.slice(keyFieldsCount);

    // Helper to get priority label and styling
    const getPriorityInfo = (priority: number | null) => {
        if (priority === 1)
            return {
                label: "High",
                color: alpha(theme.palette.error.main, 0.7),
            };
        if (priority === 2)
            return {
                label: "Med",
                color: alpha(theme.palette.warning.main, 0.7),
            };
        return null; // Don't show for lower priorities
    };

    return (
        <Card
            variant="outlined"
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: 2,
                borderColor: theme.palette.grey[300],
                bgcolor: "background.paper",
                transition: "box-shadow 0.2s ease, border-color 0.2s ease",
                "&:hover": {
                    boxShadow: theme.shadows[2],
                    borderColor: theme.palette.grey[400],
                },
            }}
        >
            <CardContent
                sx={{
                    p: 2.5,
                    "&:last-child": { pb: 2.5 },
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1,
                }}
            >
                {/* Topic name */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        color: "primary.main",
                        fontWeight: 600,
                        lineHeight: 1.3,
                        mb: 1,
                    }}
                >
                    {topic.name}
                </Typography>

                {/* Topic summary */}
                {hasSummary && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: "text.primary",
                            fontWeight: 500,
                            lineHeight: 1.5,
                            mb: 2.5,
                        }}
                    >
                        {topic.summary}
                    </Typography>
                )}

                {/* Key Fields Table */}
                {hasFields && (
                    <Box
                        sx={{
                            borderRadius: 2,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: theme.palette.grey[300],
                            mb: 2,
                        }}
                    >
                        {/* Header with icon */}
                        <Box
                            sx={{
                                px: 1.25,
                                py: 0.5,
                                bgcolor: "primary.main",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                            }}
                        >
                            <ListAltIcon
                                sx={{ fontSize: 14, color: "white" }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: "white",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.03em",
                                }}
                            >
                                Key Fields
                            </Typography>
                        </Box>

                        {/* Visible field rows */}
                        {topic.top_fields
                            .slice(0, keyFieldsCount)
                            .map((field, idx) => (
                                <Box
                                    key={field.property_id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        px: 1.25,
                                        py: 0.625,
                                        bgcolor:
                                            idx % 2 === 0
                                                ? "tertiary.main"
                                                : "background.paper",
                                        borderBottom: "1px solid",
                                        borderColor: theme.palette.grey[200],
                                        transition:
                                            "background-color 0.15s ease",
                                        "&:hover": {
                                            bgcolor:
                                                idx % 2 === 0
                                                    ? styles.tertiaryLight
                                                    : theme.palette.grey[50],
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            color: "text.secondary",
                                            lineHeight: 1.4,
                                            flex: "1 1 40%",
                                            minWidth: "40%",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {field.field_name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.primary",
                                            textAlign: "right",
                                            fontVariantNumeric: "tabular-nums",
                                            flex: "1 1 auto",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {field.field_value || "—"}
                                    </Typography>
                                </Box>
                            ))}

                        {/* Animated expandable field rows */}
                        <Collapse in={fieldsExpanded} timeout={200}>
                            {hiddenFields.map((field, idx) => (
                                <Box
                                    key={field.property_id}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        gap: 1,
                                        px: 1.25,
                                        py: 0.625,
                                        bgcolor:
                                            (keyFieldsCount + idx) % 2 === 0
                                                ? "tertiary.main"
                                                : "background.paper",
                                        borderBottom: "1px solid",
                                        borderColor: theme.palette.grey[200],
                                        transition:
                                            "background-color 0.15s ease",
                                        "&:hover": {
                                            bgcolor:
                                                (keyFieldsCount + idx) % 2 === 0
                                                    ? styles.tertiaryLight
                                                    : theme.palette.grey[50],
                                        },
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 600,
                                            color: "text.secondary",
                                            lineHeight: 1.4,
                                            flex: "1 1 40%",
                                            minWidth: "40%",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {field.field_name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "text.primary",
                                            textAlign: "right",
                                            fontVariantNumeric: "tabular-nums",
                                            flex: "1 1 auto",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {field.field_value || "—"}
                                    </Typography>
                                </Box>
                            ))}
                        </Collapse>

                        {/* Expand/collapse button */}
                        {hasMoreFields && (
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    bgcolor: theme.palette.grey[50],
                                    borderTop: "1px solid",
                                    borderColor: theme.palette.grey[200],
                                }}
                            >
                                <Button
                                    size="small"
                                    onClick={() =>
                                        setFieldsExpanded(!fieldsExpanded)
                                    }
                                    aria-expanded={fieldsExpanded}
                                    aria-label={
                                        fieldsExpanded
                                            ? "Show fewer fields"
                                            : `Show ${hiddenFields.length} more fields`
                                    }
                                    sx={{
                                        p: 0,
                                        minWidth: "auto",
                                        fontSize: "0.7rem",
                                        color: "primary.main",
                                        textTransform: "none",
                                        fontWeight: 500,
                                        "&:hover": {
                                            bgcolor: "transparent",
                                            textDecoration: "underline",
                                        },
                                    }}
                                >
                                    {fieldsExpanded
                                        ? "Show less"
                                        : `+${hiddenFields.length} more`}
                                </Button>
                            </Box>
                        )}
                    </Box>
                )}

                {/* Not Found Table */}
                {showGaps !== false &&
                    (hasGaps ? (
                        <Box
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                border: "1px solid",
                                borderColor: styles.warningLight,
                            }}
                        >
                            {/* Header with icon */}
                            <Box
                                sx={{
                                    px: 1.25,
                                    py: 0.5,
                                    bgcolor: "secondary.main",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.75,
                                }}
                            >
                                <ErrorOutlineIcon
                                    sx={{ fontSize: 14, color: "white" }}
                                />
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 600,
                                        color: "white",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.03em",
                                    }}
                                >
                                    Not Found ({sortedGaps.length})
                                </Typography>
                            </Box>

                            {/* Visible gap rows */}
                            {sortedGaps
                                .slice(0, visibleGapsCount)
                                .map((gap, idx) => {
                                    const priorityInfo = getPriorityInfo(
                                        gap.priority
                                    );
                                    return (
                                        <Box
                                            key={gap.clause_id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                px: 1.25,
                                                py: 0.625,
                                                bgcolor:
                                                    idx % 2 === 0
                                                        ? styles.warningLight
                                                        : "background.paper",
                                                borderBottom: "1px solid",
                                                borderColor:
                                                    styles.warningLight,
                                                transition:
                                                    "background-color 0.15s ease",
                                                "&:hover": {
                                                    bgcolor:
                                                        idx % 2 === 0
                                                            ? styles.warningLightHover
                                                            : theme.palette
                                                                  .grey[50],
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: "text.secondary",
                                                    lineHeight: 1.4,
                                                    wordBreak: "break-word",
                                                    flex: 1,
                                                }}
                                            >
                                                {gap.clause_title}
                                            </Typography>
                                            {priorityInfo && (
                                                <Chip
                                                    label={priorityInfo.label}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: "0.65rem",
                                                        fontWeight: 600,
                                                        bgcolor:
                                                            priorityInfo.color,
                                                        color: "white",
                                                        flexShrink: 0,
                                                        "& .MuiChip-label": {
                                                            px: 0.75,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}

                            {/* Animated expandable gap rows */}
                            <Collapse in={gapsExpanded} timeout={200}>
                                {hiddenGaps.map((gap, idx) => {
                                    const priorityInfo = getPriorityInfo(
                                        gap.priority
                                    );
                                    return (
                                        <Box
                                            key={gap.clause_id}
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-start",
                                                justifyContent: "space-between",
                                                gap: 1,
                                                px: 1.25,
                                                py: 0.625,
                                                bgcolor:
                                                    (visibleGapsCount + idx) %
                                                        2 ===
                                                    0
                                                        ? styles.warningLight
                                                        : "background.paper",
                                                borderBottom: "1px solid",
                                                borderColor:
                                                    styles.warningLight,
                                                transition:
                                                    "background-color 0.15s ease",
                                                "&:hover": {
                                                    bgcolor:
                                                        (visibleGapsCount +
                                                            idx) %
                                                            2 ===
                                                        0
                                                            ? styles.warningLightHover
                                                            : theme.palette
                                                                  .grey[50],
                                                },
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontWeight: 500,
                                                    color: "text.secondary",
                                                    lineHeight: 1.4,
                                                    wordBreak: "break-word",
                                                    flex: 1,
                                                }}
                                            >
                                                {gap.clause_title}
                                            </Typography>
                                            {priorityInfo && (
                                                <Chip
                                                    label={priorityInfo.label}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: "0.65rem",
                                                        fontWeight: 600,
                                                        bgcolor:
                                                            priorityInfo.color,
                                                        color: "white",
                                                        flexShrink: 0,
                                                        "& .MuiChip-label": {
                                                            px: 0.75,
                                                        },
                                                    }}
                                                />
                                            )}
                                        </Box>
                                    );
                                })}
                            </Collapse>

                            {/* Expand/collapse button */}
                            {hasMoreGaps && (
                                <Box
                                    sx={{
                                        px: 1.25,
                                        py: 0.5,
                                        bgcolor: "#fffbeb",
                                        borderTop: "1px solid",
                                        borderColor: styles.warningLight,
                                    }}
                                >
                                    <Button
                                        size="small"
                                        onClick={() =>
                                            setGapsExpanded(!gapsExpanded)
                                        }
                                        aria-expanded={gapsExpanded}
                                        aria-label={
                                            gapsExpanded
                                                ? "Show fewer gaps"
                                                : `Show ${hiddenGaps.length} more gaps`
                                        }
                                        sx={{
                                            p: 0,
                                            minWidth: "auto",
                                            fontSize: "0.7rem",
                                            color: "secondary.main",
                                            textTransform: "none",
                                            fontWeight: 500,
                                            "&:hover": {
                                                bgcolor: "transparent",
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        {gapsExpanded
                                            ? "Show less"
                                            : `+${hiddenGaps.length} more`}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        /* Full Coverage - Positive State */
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                borderRadius: 2,
                                bgcolor: styles.successLight,
                                border: "1px solid",
                                borderColor: theme.palette.success.light,
                            }}
                        >
                            <CheckCircleOutlineIcon
                                sx={{ fontSize: 18, color: "success.main" }}
                            />
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: 600,
                                    color: "success.main",
                                }}
                            >
                                All clauses found
                            </Typography>
                        </Box>
                    ))}

                {/* View topic details link */}
                <Box sx={{ mt: "auto", pt: 2 }}>
                    <Button
                        size="small"
                        endIcon={<ArrowForwardIcon sx={{ fontSize: 14 }} />}
                        onClick={onViewDetails}
                        aria-label={`View all clauses for ${topic.name}`}
                        sx={{
                            p: 0,
                            minWidth: "auto",
                            color: "primary.main",
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.8rem",
                            "&:hover": {
                                bgcolor: "transparent",
                                textDecoration: "underline",
                            },
                        }}
                    >
                        View all clauses
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
};
